/**
 * Hub Board & management (HW-9) — refreshes hub_board_current, the gated
 * current board per company that the hub pages read. Dry run computes and
 * reports; apply writes. Writes nothing when Cynapse's same-board duplicate
 * read is unavailable.
 */
import { apiClient } from './config.js';
import { waitForMaintenanceJob, type MaintenanceJobStatus } from './tools.js';

export interface HubBoardRow {
  person_key: string; role: string; display_name: string; role_label: string;
  role_group: 'board' | 'management'; since: string | null; last_seen: string;
  filings: number; supporting_announcement_ids: string[];
}

export interface HubDealingRow {
  trade_id: string; display_name: string; label: string; trade_date: string | null;
  notified_date: string; date_is_notified: boolean; shares: number | null; value_aud: number | null;
  holding_after: number | null; interest: 'direct' | 'indirect' | null; supporting_announcement_id: string;
}

export interface HubHolderRow {
  filing_id: string; holder_name_as_filed: string; form_type: string; pct: number;
  change_date: string | null; notified_date: string; supporting_announcement_id: string;
}

export interface HubBlockCounts { rows: number; entities: number; rejected: Record<string, number> }

export interface HubBoardResult {
  dry_run: boolean; entities: number; entities_with_rows: number; rows: number;
  rejected: Record<string, number>; mixed_record_skipped: number; removed_out_of_scope: number;
  stale_cutoff: string; dealing_cutoff?: string;
  dealings?: HubBlockCounts; holders?: HubBlockCounts;
  samples: Record<string, { board: HubBoardRow[]; dealings: HubDealingRow[]; holders: HubHolderRow[] }>;
  error?: string;
}

export async function runHubBoardRefresh(
  dryRun: boolean, codes?: string,
  onProgress?: (s: MaintenanceJobStatus) => void,
): Promise<HubBoardResult> {
  const { data } = await apiClient.post<{ job_id: string; note?: string }>(
    '/tools/hub-board-refresh', null, { params: { dry_run: dryRun, ...(codes ? { codes } : {}) } },
  );
  if (data.note) throw new Error('A hub board refresh is already running — try again when it finishes.');
  return waitForMaintenanceJob<HubBoardResult>(data.job_id, onProgress, 2000);
}
